import { Component, Element, h, Host, Listen, Prop, State, Watch } from "@stencil/core";
import { applySchemeDelegate, ColorableComponent, setCssVariablesDelegate } from "../../common/colorable-component";
import { CaptionSchemeDefinition, CaptionSchemeName } from "./constants/caption-types";
import { CAPTION_SCHEME_CONFIG, CAPTION_SCHEME_MAP } from "./constants/caption-constants";
import pfx from "../../utils/style-class-prefix-helper";
import { isDesktopLarge, isMobile, isTablet } from "../../utils/screen-helper";
import { CaptionSchemeConfiguration } from "../../constants/colors";
import {
  ContainerBreakpointComponent,
  initContainerBreakpointsDelegate
} from "../../common/container-breakpoint-component";

@Component({
  tag: "eon-ui-caption",
  styleUrl: "caption.scss",
  shadow: true
})
export class Caption
  implements ColorableComponent<CaptionSchemeConfiguration<CaptionSchemeDefinition>>, ContainerBreakpointComponent {
  schemeConfig: CaptionSchemeConfiguration<CaptionSchemeDefinition> = CAPTION_SCHEME_CONFIG;
  schemeMap = CAPTION_SCHEME_MAP;
  contentElement: HTMLElement;
  copyrightElement: HTMLElement;
  wrapperElement: HTMLElement;

  /**
   * Array that defines component specific container breakpoints.
   */
  containerBreakpoints = [1200, 800, 400];

  @State() isMobile: boolean;
  @State() isTablet: boolean;
  @State() isDesktopLarge: boolean;
  @State() copyrightVerticalPlacement: boolean;

  /**
   * The components active scheme. If {@link scheme} is set, it will be set to the corresponding scheme.
   * If not set, the component will try to calculate it based on the nearest enclosing background information given by inner-background attribute or eon-ui-background component.
   */
  @State() activeScheme: CaptionSchemeDefinition;

  @Element() hostElement: HTMLEonUiCaptionElement;

  /**
   * The name of the color scheme of the component.
   * If not set, the component will try to calculate it based on the nearest enclosing background information given by inner-background attribute or eon-ui-background component.
   */
  @Prop({ reflect: true }) scheme: CaptionSchemeName;

  /**
   * Caption content
   */
  @Prop({ reflect: true, mutable: true }) caption: string;

  /**
   * copyright content
   */
  @Prop({ reflect: true }) copyright: string;

  initContainerBreakpoints(): void {
    initContainerBreakpointsDelegate(this.hostElement, this.containerBreakpoints);
  }

  componentWillLoad() {
    this.applyScheme();
    this.initContainerBreakpoints();
  }

  componentDidLoad() {
    this.setCssVariables();
    this.windowOnResize();
  }

  @Watch("scheme")
  applyScheme() {
    applySchemeDelegate(this);
  }

  @Watch("activeScheme")
  setCssVariables(): void {
    setCssVariablesDelegate(this);
  }

  @Listen("resize", { target: "window" })
  windowOnResize() {
    this.isMobile = isMobile();
    this.isTablet = isTablet();
    this.isDesktopLarge = isDesktopLarge();
    this.handleCaptionPlacement();
  }

  @Watch("caption")
  handleCaptionPlacement() {
    window.setTimeout(() => {
      // only if caption and copyright exist start with calculation
      if (this.contentElement && this.copyrightElement) this.copyrightVerticalPlacement = this.contentIsWrapped();
    }, 0);
  }

  private contentIsWrapped() {
    return (
      this.wrapperElement.offsetWidth -
        parseInt(window.getComputedStyle(this.wrapperElement).getPropertyValue("padding-right")) * 2 <
      this.contentElement.querySelector("eon-ui-text").offsetWidth +
        this.copyrightElement.querySelector("eon-ui-text").offsetWidth +
        (this.copyrightVerticalPlacement
          ? 16 * 2
          : parseInt(window.getComputedStyle(this.contentElement).getPropertyValue("padding-right")) * 2) +
        1
    );
  }

  render() {
    return (
      <Host>
        {(this.caption || this.copyright) && (
          <div
            class={pfx({
              ["caption-copyright-wrapper"]: true,
              ["vertical"]: this.copyrightVerticalPlacement,
              ["no-caption"]: !this.caption,
              ["caption-and-copyright"]: this.caption && this.copyright
            })}
            ref={(el) => (this.wrapperElement = el as HTMLElement)}
          >
            {this.caption && (
              <div
                class={pfx({
                  ["caption-content"]: true,
                  ["no-copyright"]: !this.copyright
                })}
                ref={(el) => (this.contentElement = el as HTMLElement)}
              >
                <eon-ui-text font-weight="500">{this.caption}</eon-ui-text>
              </div>
            )}
            {this.copyright && (
              <div class={pfx("copyright-content")} ref={(el) => (this.copyrightElement = el as HTMLElement)}>
                <eon-ui-text>{this.copyright}</eon-ui-text>
              </div>
            )}
          </div>
        )}
      </Host>
    );
  }
}
